import { useState, useEffect } from 'react'
import { Form, Input, Button, Avatar, Upload, Row, Col, message } from 'antd'
import { UserOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons'
import { userApi } from '../../services/api'

const PROFILE_STORAGE_KEY = 'asset_settings_profile'
const AVATAR_STORAGE_KEY = 'asset_settings_avatar'
const defaultProfile = {
  name: '燕子',
  email: 'yanzi@example.com',
  phone: '13800138000',
  department: '技术部',
  position: '数据分析师',
  bio: '专注于数据可视化和资产管理的技术专家',
}

const SettingsProfile = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState('')
  const [avatarLoading, setAvatarLoading] = useState(false)

  useEffect(() => {
    userApi.getProfile()
      .then((res: any) => {
        const data = res?.data ?? res
        if (data && typeof data === 'object') {
          form.setFieldsValue({
            name: data.name ?? data.nickname ?? defaultProfile.name,
            email: data.email ?? defaultProfile.email,
            phone: data.phone ?? defaultProfile.phone,
            department: data.department ?? defaultProfile.department,
            position: data.position ?? defaultProfile.position,
            bio: data.bio ?? defaultProfile.bio,
          })
          const avatar = (data as any).avatar ?? (data as any).avatarUrl ?? (data as any).avatar_url
          if (typeof avatar === 'string' && avatar) setAvatarUrl(avatar)
        }
      })
      .catch(() => {
        try {
          const profileJson = localStorage.getItem(PROFILE_STORAGE_KEY)
          if (profileJson) form.setFieldsValue(JSON.parse(profileJson))
          else form.setFieldsValue(defaultProfile)
        } catch {
          form.setFieldsValue(defaultProfile)
        }
      })
    const savedAvatar = localStorage.getItem(AVATAR_STORAGE_KEY)
    if (savedAvatar) setAvatarUrl(savedAvatar)
  }, [form])

  const handleSaveProfile = () => {
    setLoading(true)
    form.validateFields().then(values => {
      userApi.updateProfile(values)
        .then(() => {
          localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(values))
          message.success('个人资料保存成功')
        })
        .catch(() => {
          localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(values))
          message.success('个人资料已保存到本地')
        })
        .finally(() => setLoading(false))
    }).catch(() => setLoading(false))
  }

  const handleAvatarChange = (info: any) => {
    const { file } = info
    const rawFile = file?.originFileObj ?? file
    if (!rawFile || !(rawFile instanceof File)) return
    const isImage = /^image\/(jpeg|png|webp)$/i.test(rawFile.type)
    if (!isImage) {
      message.error('仅支持 JPG、PNG、WEBP 格式')
      return
    }
    const isLt2M = rawFile.size / 1024 / 1024 < 2
    if (!isLt2M) {
      message.error('图片大小不能超过 2MB')
      return
    }
    setAvatarLoading(true)
    const reader = new FileReader()
    reader.addEventListener('load', () => {
      const result = reader.result as string
      setAvatarUrl(result)
      try { localStorage.setItem(AVATAR_STORAGE_KEY, result) } catch { message.warning('头像未保存到本地') }
      setAvatarLoading(false)
      message.success('头像已更换')
    })
    reader.addEventListener('error', () => { setAvatarLoading(false); message.error('读取图片失败') })
    reader.readAsDataURL(rawFile)
  }

  const beforeAvatarUpload = (file: File) => {
    const isImage = /^image\/(jpeg|png|webp)$/i.test(file.type)
    if (!isImage) { message.error('仅支持 JPG、PNG、WEBP 格式'); return Upload.LIST_IGNORE }
    if (file.size / 1024 / 1024 >= 2) { message.error('图片大小不能超过 2MB'); return Upload.LIST_IGNORE }
    return false
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">个人资料</h1>
        <p className="text-gray-600">管理您的个人基本信息</p>
      </div>
      <Row gutter={[32, 32]}>
        <Col xs={24} lg={8}>
          <div className="text-center">
            <Avatar size={128} src={avatarUrl} icon={<UserOutlined />} className="mb-4 border-4 border-white shadow-lg" />
            <Upload name="avatar" showUploadList={false} onChange={handleAvatarChange} beforeUpload={beforeAvatarUpload} accept=".jpg,.jpeg,.png,.webp" disabled={avatarLoading}>
              <Button icon={<UploadOutlined />} loading={avatarLoading}>更换头像</Button>
            </Upload>
            <div className="text-sm text-gray-500 mt-2">支持 JPG、PNG 格式，大小不超过 2MB</div>
          </div>
        </Col>
        <Col xs={24} lg={16}>
          <Form form={form} layout="vertical" initialValues={defaultProfile}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="name" label="姓名" rules={[{ required: true, message: '请输入姓名' }]}>
                  <Input placeholder="请输入姓名" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="email" label="邮箱" rules={[{ required: true, message: '请输入邮箱' }, { type: 'email', message: '请输入有效的邮箱地址' }]}>
                  <Input placeholder="请输入邮箱" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="phone" label="手机号" rules={[{ required: true, message: '请输入手机号' }]}>
                  <Input placeholder="请输入手机号" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="department" label="部门" rules={[{ required: true, message: '请输入部门' }]}>
                  <Input placeholder="请输入部门" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="position" label="职位" rules={[{ required: true, message: '请输入职位' }]}>
              <Input placeholder="请输入职位" />
            </Form.Item>
            <Form.Item name="bio" label="个人简介">
              <Input.TextArea rows={4} placeholder="请输入个人简介" maxLength={200} showCount />
            </Form.Item>
            <Form.Item>
              <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveProfile} loading={loading}>保存更改</Button>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </div>
  )
}

export default SettingsProfile
